import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SCRIPT_URLS = [
  'https://www.google.com/recaptcha/enterprise.js?render=explicit',
  'https://www.recaptcha.net/recaptcha/enterprise.js?render=explicit',
];
const pendingExecutions = new Map();
let scriptPromise = null;

const isBrowser = typeof window !== 'undefined';

const getRuntimeConfig = () => {
  if (!isBrowser) {
    return {};
  }
  return window.__CB_CONFIG__ || window.__PUBLIC_CONFIG__ || {};
};

const getSiteKey = () => {
  const cfg = getRuntimeConfig();
  return cfg.recaptchaSiteKey || cfg.RECAPTCHA_SITE_KEY || '';
};

const getRecaptchaApi = () => {
  if (!isBrowser || !window.grecaptcha) {
    return null;
  }
  return window.grecaptcha.enterprise || window.grecaptcha;
};

const waitForGrecaptcha = () =>
  new Promise((resolve, reject) => {
    if (!isBrowser) {
      reject(new Error('reCAPTCHA is unavailable in this context'));
      return;
    }

    const tick = () => {
      const api = getRecaptchaApi();
      if (api && typeof api.render === 'function') {
        if (typeof api.ready === 'function') {
          api.ready(() => resolve(api));
        } else {
          resolve(api);
        }
        return;
      }
      setTimeout(tick, 25);
    };

    tick();
  });

const loadRecaptchaScript = () => {
  if (!isBrowser) {
    return Promise.reject(new Error('reCAPTCHA can only load in the browser'));
  }

  const api = getRecaptchaApi();
  if (api && typeof api.render === 'function') {
    return Promise.resolve(api);
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const loadNext = (index) => {
        if (index >= SCRIPT_URLS.length) {
          reject(new Error('Failed to load reCAPTCHA script'));
          return;
        }

        const src = SCRIPT_URLS[index];
        const existing = document.querySelector(`script[src="${src}"]`);

        if (existing) {
          if (existing.dataset.loaded === 'true') {
            resolve();
            return;
          }

          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => loadNext(index + 1), { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = () => {
          script.remove();
          loadNext(index + 1);
        };
        document.head.appendChild(script);
      };

      loadNext(0);
    });
  }

  return scriptPromise.then(() => waitForGrecaptcha());
};

const finalizeExecution = (widgetId) => {
  pendingExecutions.delete(widgetId);
  const api = getRecaptchaApi();
  if (api && typeof api.reset === 'function') {
    try {
      api.reset(widgetId);
    } catch (err) {
      console.warn('Failed to reset reCAPTCHA widget', err);
    }
  }
};

const renderRecaptcha = (element, siteKey, action, options, setVisibleToken) => {
  if (!element) {
    throw new Error('Missing element to mount reCAPTCHA');
  }
  if (!siteKey) {
    throw new Error('Missing reCAPTCHA site key');
  }

  let widgetId;
  const handleToken = (token) => {
    if (typeof setVisibleToken === 'function') {
      setVisibleToken(token);
    }
    const resolver = pendingExecutions.get(widgetId);
    if (resolver) {
      resolver.resolve(token);
      finalizeExecution(widgetId);
    }
  };

  const handleError = (reason) => {
    const resolver = pendingExecutions.get(widgetId);
    if (resolver) {
      resolver.reject(reason instanceof Error ? reason : new Error(String(reason || 'reCAPTCHA error')));
      finalizeExecution(widgetId);
    }
  };

  const renderOptions = {
    sitekey: siteKey,
    action,
    size: options.size,
    theme: options.theme,
    callback: handleToken,
    'error-callback': () => handleError(new Error('reCAPTCHA verification failed')),
    'expired-callback': () => handleError(new Error('reCAPTCHA expired, please retry')),
  };

  if (options.size === 'invisible') {
    renderOptions.badge = options.badge;
  }

  const api = getRecaptchaApi();
  if (!api || typeof api.render !== 'function') {
    throw new Error('reCAPTCHA API is unavailable');
  }

  widgetId = api.render(element, renderOptions);
  return widgetId;
};

const executeWidget = (widgetId) =>
  new Promise((resolve, reject) => {
    if (widgetId === null || widgetId === undefined) {
      reject(new Error('reCAPTCHA widget not ready'));
      return;
    }

    pendingExecutions.set(widgetId, { resolve, reject });

    try {
      const api = getRecaptchaApi();
      if (!api || typeof api.execute !== 'function') {
        throw new Error('Unable to execute reCAPTCHA');
      }
      api.execute(widgetId);
    } catch (err) {
      pendingExecutions.delete(widgetId);
      reject(err instanceof Error ? err : new Error('Unable to execute reCAPTCHA'));
    }
  });

const defaultOptions = {
  size: 'invisible', // 'invisible', 'normal', or 'compact'
  theme: 'light',
  badge: 'bottomright',
};

export default function useRecaptcha(action = 'submit', options = {}) {
  const mergedOptions = { ...defaultOptions, ...options };
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const widgetOptionsRef = useRef(mergedOptions);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [visibleToken, setVisibleToken] = useState(null);

  useEffect(() => {
    widgetOptionsRef.current = mergedOptions;
  }, [mergedOptions.size, mergedOptions.theme, mergedOptions.badge]);

  const siteKey = useMemo(() => getSiteKey(), []);

  useEffect(() => {
    if (!isBrowser) {
      setError(new Error('reCAPTCHA is only available in the browser'));
      return;
    }

    if (!siteKey) {
      setError(new Error('Missing reCAPTCHA site key'));
      return;
    }

    let cancelled = false;

    loadRecaptchaScript()
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }
        widgetIdRef.current = renderRecaptcha(
          containerRef.current,
          siteKey,
          action,
          widgetOptionsRef.current,
          widgetOptionsRef.current.size === 'invisible' ? null : setVisibleToken
        );
        if (!cancelled) {
          setReady(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load reCAPTCHA'));
        }
      });

    return () => {
      cancelled = true;
      const api = getRecaptchaApi();
      if (widgetIdRef.current !== null && api?.reset) {
        try {
          api.reset(widgetIdRef.current);
        } catch (err) {
          console.warn('Failed to reset reCAPTCHA on cleanup', err);
        }
      }
    };
  }, [siteKey, action]);

  const execute = useCallback(async () => {
    if (!siteKey) {
      throw new Error('Missing reCAPTCHA site key');
    }
    if (!ready || widgetIdRef.current === null || widgetIdRef.current === undefined) {
      throw new Error('reCAPTCHA is still initializing');
    }

    const { size } = widgetOptionsRef.current;

    if (size === 'invisible') {
      const token = await executeWidget(widgetIdRef.current);
      return token;
    }

    if (visibleToken) {
      const token = visibleToken;
      setVisibleToken(null);
      const api = getRecaptchaApi();
      if (api?.reset) {
        try {
          api.reset(widgetIdRef.current);
        } catch (err) {
          console.warn('Failed to reset reCAPTCHA checkbox', err);
        }
      }
      return token;
    }

    throw new Error('Please complete the reCAPTCHA challenge.');
  }, [ready, siteKey, visibleToken]);

  const challengeCompleted = widgetOptionsRef.current.size === 'invisible' ? true : Boolean(visibleToken);

  return {
    action,
    containerRef,
    execute,
    ready,
    error,
    siteKey,
    challengeCompleted,
    size: widgetOptionsRef.current.size,
  };
}
